import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { PackagingSvhcAuditStoredEntry } from '@/shared/hooks/api/packagingSuppliers'
import { PackagingAssessmentRunDetailSheet } from './PackagingAssessmentRunDetailSheet'
import { formatRunTimestamp } from './PackagingFieldCrossCheckRunResults'
import { PackagingSvhcAuditRunResults } from './PackagingSvhcAuditRunResults'
import {
  packagingItemSvhcAuditStatusLabelKey,
  packagingSvhcAuditVerdictLabelKey,
} from './packagingItemSvhcAuditFilter'
import type { PackagingItemSvhcAuditStatus } from './packagingItemSvhcAuditOverviewClient'
import { svhcAuditVerdictToOverviewStatus } from './packagingItemSvhcAuditOverviewClient'
import { isManualOverrideEntry } from './packagingAuditOverrideValidation'

type PackagingSvhcAuditHistoryTableProps = {
  history: PackagingSvhcAuditStoredEntry[]
  locale: string
}

function verdictBadgeVariant(
  status: PackagingItemSvhcAuditStatus
): 'success' | 'danger' | 'warning' | 'secondary' {
  if (status === 'passed') {
    return 'success'
  }
  if (status === 'flagged') {
    return 'warning'
  }
  if (status === 'failed') {
    return 'danger'
  }
  return 'secondary'
}

function SvhcAuditHistorySummary({
  entry,
}: {
  entry: PackagingSvhcAuditStoredEntry
}) {
  const { t } = useTranslation('packagingSuppliers')
  const status = svhcAuditVerdictToOverviewStatus(entry.verdict)

  return (
    <div className='flex flex-wrap items-center gap-2'>
      <Badge variant={verdictBadgeVariant(status)}>
        {t(packagingItemSvhcAuditStatusLabelKey(status))}
      </Badge>
      {isManualOverrideEntry(entry) ? (
        <span className='text-sm text-muted-foreground'>
          {entry.manualOverride?.reason ?? entry.reasoningEn}
        </span>
      ) : (
        <span className='text-sm text-muted-foreground'>
          {t(packagingSvhcAuditVerdictLabelKey(entry.verdict))}
        </span>
      )}
    </div>
  )
}

export function PackagingSvhcAuditHistoryTable({
  history,
  locale,
}: PackagingSvhcAuditHistoryTableProps) {
  const { t } = useTranslation('packagingSuppliers')
  const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(
    null
  )

  const selectedEntry = history.find(
    (entry) => entry.timestamp === selectedTimestamp
  )
  const selectedTitle =
    selectedEntry != null
      ? formatRunTimestamp(selectedEntry.timestamp, locale)
      : ''

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {t('itemDetail.svhcAudit.history.columnRun')}
            </TableHead>
            <TableHead>
              {t('itemDetail.svhcAudit.history.columnSummary')}
            </TableHead>
            <TableHead className='w-[1%] text-end'>
              <span className='sr-only'>
                {t('itemDetail.svhcAudit.history.columnActions')}
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((entry) => {
            const runLabel = formatRunTimestamp(entry.timestamp, locale)
            return (
              <TableRow key={entry.timestamp}>
                <TableCell className='font-medium'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <span>{runLabel}</span>
                    {isManualOverrideEntry(entry) ? (
                      <Badge variant='outline'>
                        {t('itemDetail.manualOverride.tag')}
                      </Badge>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell>
                  <SvhcAuditHistorySummary entry={entry} />
                </TableCell>
                <TableCell className='text-end'>
                  <Button
                    type='button'
                    variant='link'
                    className='h-auto px-0'
                    onClick={() => {
                      setSelectedTimestamp(entry.timestamp)
                    }}
                  >
                    {t('itemDetail.svhcAudit.history.viewResults')}
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <PackagingAssessmentRunDetailSheet
        open={selectedEntry != null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTimestamp(null)
          }
        }}
        title={selectedTitle}
      >
        {selectedEntry != null ? (
          isManualOverrideEntry(selectedEntry) ? (
            <p className='text-sm text-muted-foreground whitespace-pre-wrap'>
              {selectedEntry.manualOverride?.reason ?? selectedEntry.reasoningEn}
            </p>
          ) : (
            <PackagingSvhcAuditRunResults result={selectedEntry} />
          )
        ) : null}
      </PackagingAssessmentRunDetailSheet>
    </>
  )
}
